import {
  breakpointSizes,
  getBreakpointDown,
  getFontCss,
  LoadingBoxBottom,
  styleConstants,
  Tag,
  TagSizes,
  themeConstants,
} from '@paywithextend/components'
import ReportCycleUpsellSvg from 'ext-ui/svg/illustration/ReportCycleUpsellSvg'
import moment from 'moment'
import React, { useCallback } from 'react'
import { useSelector } from 'react-redux'
import BackgroundPage from 'src/components/BackgroundPage/BackgroundPage'
import BackgroundPageContent from 'src/components/BackgroundPageContent/BackgroundPageContent'
import Box from 'src/components/Box/Box'
import ListBox from 'src/components/ListBox/ListBox'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import NotFoundBackgroundPage from 'src/components/NotFoundBackgroundPage/NotFoundBackgroundPage'
import SvgChevronRight from 'src/components/Svgs/SvgChevronRight'
import UserAvatarAndName from 'src/components/UserAvatarAndName/UserAvatarAndName'
import SaasUpgradeOrContent from 'src/feature/SaasUpgradeOrContent/SaasUpgradeOrContent'
import { useWindowScrollBottomHandler } from 'src/hooks'
import Crncy from 'src/localization/Crncy'
import Trns, { useTranslate } from 'src/localization/Trns'
import { OrgExpenseReport } from 'src/models/orgExpenseReports.models'
import { UserFeature } from 'src/models/user.models'
import Lnk from 'src/navigation/Lnk'
import { orgExpenseReportUrl } from 'src/navigation/urls'
import {
  useQueryOrgExpenseReportCycle,
  useQueryOrgExpenseReportCycleReports,
} from 'src/queries/orgExpenseReports.queries'
import { selectUserListModelFromState } from 'src/redux/selectors/auth.selectors'
import { selectExpenseReportsHistoricalReportsCycleIdFromRoute } from 'src/redux/selectors/data/orgExpenseReports.selectors'
import { toDollars } from 'src/utils/currency.utils'
import { css } from 'styled-components'

import styles from './StatementsList.style'

const OrgExpenseReportsHistoricalReports: React.FC = () => {
  const translate = useTranslate()
  const cycleId = useSelector(
    selectExpenseReportsHistoricalReportsCycleIdFromRoute,
  )
  const { data: cycle, error: cycleError } = useQueryOrgExpenseReportCycle({
    cycleId,
  })
  const user = useSelector(selectUserListModelFromState)

  const {
    data: cycleReports,
    hasNextPage,
    isFetching,
    error: cycleReportsError,
    fetchNextPage,
  } = useQueryOrgExpenseReportCycleReports({ cycleId })

  const handleScroll = useCallback((): void => {
    if (hasNextPage && !isFetching && !cycleReportsError && !cycleError) {
      fetchNextPage()
    }
  }, [hasNextPage, isFetching, cycleReportsError, cycleError, fetchNextPage])
  useWindowScrollBottomHandler(handleScroll)

  const userAndCardName =
    cycle !== undefined ? (
      <div
        css={css`
          display: flex;
          flex-direction: column;
          width: 100%;
        `}
      >
        <UserAvatarAndName
          textCss={css`
            ${getFontCss(
              14,
              themeConstants.colors.font_primary,
              styleConstants.fontFamilies.regular,
            )};
          `}
          user={{
            id: cycle.accountUserId,
            fullNameOrEmail:
              cycle.accountUserFullName || cycle.accountUserEmail,
            initials:
              `${cycle.accountUserFirstName?.slice(0, 1)}${cycle.accountUserLastName?.slice(0, 1)}` ||
              cycle.accountUserEmail[0],
            avatarUrl: cycle.accountUserAvatarUrl,
            hasFullName: true,
          }}
        />
        <span
          css={css`
            margin-top: 12px;
            margin-bottom: 8px;
          `}
        >
          <span
            css={css`
              ${getFontCss(
                28,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.medium,
              )};
            `}
          >
            <span
              css={css`
                padding-inline-end: 12px;
              `}
            >
              {cycle?.accountName}
            </span>
            <Tag text={cycle.accountLast4.toString()} size={TagSizes.Medium} />
          </span>
        </span>
      </div>
    ) : undefined

  if (!user?.hasOrgBookkeeperPermissions) {
    return <NotFoundBackgroundPage />
  }

  return (
    <SaasUpgradeOrContent
      illustration={<ReportCycleUpsellSvg />}
      title={translate('Expense Report Cycle')}
      description={translate(
        "For each billing cycle, you'll receive an automatically generated expense report matching your bank statement—complete with all virtual* and physical card transactions (if applicable).",
      )}
      disclaimer={
        <Trns
          source="*Virtual cards available for ${selectIssuersLink}"
          expressions={{
            selectIssuersLink: (
              <Lnk
                className="underline-link"
                href="https://support.paywithextend.com/hc/en-us/sections/35632429037079-Registering-a-Card-Account"
                ariaLabel={translate('select issuers')}
              >
                {translate('select issuers')}
              </Lnk>
            ),
          }}
        />
      }
      ctaDetailSrc={
        'Upgrade to ${tier} today and receive automatically generated expense reports synced with your billing cycle.'
      }
      feature={UserFeature.AUTOMATED_EXPENSE_REPORTS}
    >
      {!cycleError ? (
        <BackgroundPage className="StatementsList" css={styles}>
          <BackgroundPageContent noStackingIndex>
            {cycle === undefined ? (
              <LoadingBackgroundPage />
            ) : (
              <>
                {userAndCardName}
                <Box className="StatementsList statements-container">
                  <div className="StatementsList statements">
                    {cycleReports?.map((report: OrgExpenseReport) => {
                      return (
                        <ListBox
                          key={report.id}
                          id={'report-' + report.id}
                          linkProps={{
                            to: orgExpenseReportUrl(cycleId, report.id),
                            ariaLabelSource: 'Account Expense Report',
                          }}
                          cells={[
                            {
                              label: <Trns source={'Statement Date'} />,
                              content: (
                                <div id="title">
                                  {moment.utc(report.since).format('LL') +
                                    ' - ' +
                                    moment.utc(report.until).format('LL')}
                                </div>
                              ),
                            },
                            report.totalSpentCents !== undefined
                              ? {
                                  label: (
                                    <div
                                      css={css`
                                        text-align: right;
                                      `}
                                    >
                                      <Trns source={'Spend'} />
                                    </div>
                                  ),
                                  content: (
                                    <Crncy
                                      currency={report.accountCurrency}
                                      amount={toDollars(report.totalSpentCents)}
                                    />
                                  ),
                                }
                              : {
                                  label: <></>,
                                  content: <></>,
                                },
                          ]}
                          icon={
                            <div
                              css={css`
                                display: flex;
                                align-items: center;
                              `}
                            >
                              <SvgChevronRight
                                id={'chevron'}
                                css={css`
                                  transition: transform
                                    ${styleConstants.transitionSpeed.fast}
                                    ease-out;
                                  transform: scale(calc(20 / 24));
                                  ${getBreakpointDown(breakpointSizes.SM)(css`
                                    display: none;
                                  `)}
                                `}
                              />
                            </div>
                          }
                        />
                      )
                    })}
                  </div>
                  {isFetching && <LoadingBoxBottom />}
                </Box>
              </>
            )}
          </BackgroundPageContent>
        </BackgroundPage>
      ) : (
        <NotFoundBackgroundPage />
      )}
    </SaasUpgradeOrContent>
  )
}

export default OrgExpenseReportsHistoricalReports
